import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  TrendingUp,
  Globe,
  Receipt,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  RefreshCw,
  Server,
  ShoppingBag,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { ShopierModal } from './ShopierModal';
import {
  BillingOrder,
  AIVisibilitySubscription,
  UserSubscriptionRecord,
  SiteSubscription,
  SubscriptionPlanId,
} from '../../types';
import { SUBSCRIPTION_PLANS } from '../../config/constants';

interface CustomerSubscriptionsViewProps {
  onNavigate?: (view: string) => void;
}

export const CustomerSubscriptionsView: React.FC<CustomerSubscriptionsViewProps> = ({ onNavigate }) => {
  const { currentUser } = useAuth();
  const { activeSite, sites } = useSite();

  const [loading, setLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<BillingOrder[]>([]);
  const [aiSub, setAiSub] = useState<AIVisibilitySubscription | null>(null);
  const [websiteSubs, setWebsiteSubs] = useState<UserSubscriptionRecord[]>([]);

  // Renewal / Checkout Modal State
  const [isCheckoutOpen, setIsCheckoutOpen] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<SubscriptionPlanId>('PLAN_CORP_ANNUAL');
  const [checkoutTitle, setCheckoutTitle] = useState<string>('Abonelik Yenileme');
  const [checkoutAmount, setCheckoutAmount] = useState<number>(15000);
  const [checkoutPeriod, setCheckoutPeriod] = useState<string>('Yıllık');

  const userId = currentUser?.uid || '';

  const loadData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [userOrders, aiVisibility, userWebsiteSubs] = await Promise.all([
        dbService.getOrdersByUser(userId),
        dbService.getAIVisibilitySubscription(userId),
        dbService.getUserSubscriptions(userId),
      ]);

      setOrders(userOrders);
      setAiSub(aiVisibility);
      setWebsiteSubs(userWebsiteSubs);
    } catch (err) {
      console.error('Error loading subscriptions data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [userId]);

  const handleOpenRenew = (planId: SubscriptionPlanId, title: string, amount: number, period: string) => {
    setSelectedPlanId(planId);
    setCheckoutTitle(title);
    setCheckoutAmount(amount);
    setCheckoutPeriod(period);
    setIsCheckoutOpen(true);
  };

  const now = Date.now();

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 animate-fade-in text-slate-900">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <CreditCard className="w-6 h-6 text-blue-600" />
            <span>Aboneliklerim & Hizmet Yönetimi</span>
          </h1>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            Hesabınıza tanımlı tüm aktif SaaS paketleri, ek hizmetler ve Shopier ödeme geçmişiniz.
          </p>
        </div>

        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold flex items-center gap-2 transition self-start sm:self-auto cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      {/* 1. ACTIVE SUBSCRIPTIONS GRID */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <span>Kayıtlı Abonelikleriniz</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Website Subscription */}
          {(() => {
            const hasActiveWebsiteSub = websiteSubs.some((s) => s.status === 'ACTIVE' && s.expireDate > now);
            const primarySiteSub = activeSite?.subscription;
            const isSubActive = primarySiteSub?.status === 'ACTIVE' && (primarySiteSub?.expireDate || 0) > now;
            const expireDate = primarySiteSub?.expireDate || (websiteSubs[0]?.expireDate || 0);
            const planId = (primarySiteSub?.planId || websiteSubs[0]?.planId || 'PLAN_CORP_ANNUAL') as SubscriptionPlanId;
            const planConfig = SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS['PLAN_CORP_ANNUAL'];

            return (
              <div className="bg-white rounded-3xl p-6 border border-slate-200/90 shadow-xs flex flex-col justify-between space-y-5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="p-2.5 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
                      <Globe className="w-5 h-5" />
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                      isSubActive || hasActiveWebsiteSub
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      <CheckCircle2 className="w-3 h-3" />
                      {isSubActive || hasActiveWebsiteSub ? 'Aktif Paket' : 'Süresi Dolmuş / Deneme'}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-bold text-base text-slate-900">
                      {planConfig.name}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {planConfig.description}
                    </p>
                  </div>

                  <div className="p-3 rounded-2xl bg-slate-50 space-y-1.5 text-xs">
                    <div className="flex justify-between text-slate-600">
                      <span>Bitiş Tarihi:</span>
                      <strong className="text-slate-900">
                        {expireDate ? new Date(expireDate).toLocaleDateString('tr-TR') : 'Belirtilmedi'}
                      </strong>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Paket Ücreti:</span>
                      <strong className="text-slate-900">
                        {planConfig.priceTRY.toLocaleString('tr-TR')} TL / {planConfig.billingPeriod}
                      </strong>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Ödeme Sağlayıcı:</span>
                      <strong className="text-slate-900">Shopier</strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleOpenRenew(planId, planConfig.name, planConfig.priceTRY, planConfig.billingPeriod)}
                  className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Aboneliği Yenile / Yükselt</span>
                </button>
              </div>
            );
          })()}

          {/* Card 2: AI Search Visibility Subscription */}
          {(() => {
            const isAiActive = Boolean(aiSub && aiSub.status === 'active' && aiSub.endDate > now);

            return (
              <div className="bg-white rounded-3xl p-6 border border-slate-200/90 shadow-xs flex flex-col justify-between space-y-5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                      <Sparkles className="w-5 h-5" />
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                      isAiActive
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-slate-100 text-slate-600 border border-slate-200'
                    }`}>
                      {isAiActive ? 'Aktif Hizmet' : 'Tanımlı Değil'}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-bold text-base text-slate-900">
                      AI Arama Görünürlüğü
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      ChatGPT Search ve Gemini botları için JSON-LD şemaları ve varlık optimizasyonu.
                    </p>
                  </div>

                  <div className="p-3 rounded-2xl bg-slate-50 space-y-1.5 text-xs">
                    <div className="flex justify-between text-slate-600">
                      <span>Bitiş Tarihi:</span>
                      <strong className="text-slate-900">
                        {aiSub?.endDate ? new Date(aiSub.endDate).toLocaleDateString('tr-TR') : 'Yok'}
                      </strong>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Hizmet Ücreti:</span>
                      <strong className="text-slate-900">5.000 TL / Yıl</strong>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Ödeme Sağlayıcı:</span>
                      <strong className="text-slate-900">Shopier</strong>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {onNavigate && (
                    <button
                      type="button"
                      onClick={() => onNavigate('ai-visibility')}
                      className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs transition cursor-pointer"
                    >
                      Paneli Aç
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenRenew('PLAN_AI_VISIBILITY_ANNUAL', 'AI Arama Görünürlüğü', 5000, 'Yıllık')}
                    className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>{isAiActive ? 'Yenile' : 'Satın Al'}</span>
                  </button>
                </div>
              </div>
            );
          })()}

        </div>
      </div>

      {/* 2. REAL PAYMENT HISTORY (ÖDEME GEÇMİŞİ) */}
      <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/90 shadow-xs space-y-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              <span>Ödeme Geçmişi & İşlem Kayıtları</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Shopier üzerinden gerçekleştirilen doğrulanmış ödemeleriniz ve dekont referansları.
            </p>
          </div>

          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
            {orders.length} İşlem
          </span>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div className="text-sm font-semibold text-slate-700">Henüz Bir Ödeme Kaydı Bulunmuyor</div>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Shopier üzerinden yapacağınız paket alımları ve yenilemeler anında bu tabloda listelenecektir.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-400 uppercase tracking-wider text-[10px]">
                  <th className="pb-3 font-semibold">Tarih</th>
                  <th className="pb-3 font-semibold">Ürün / Hizmet</th>
                  <th className="pb-3 font-semibold">Tutar</th>
                  <th className="pb-3 font-semibold">Durum</th>
                  <th className="pb-3 font-semibold">Shopier Sipariş ID</th>
                  <th className="pb-3 font-semibold text-right">Referans</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-50/80 transition">
                    <td className="py-3.5 text-slate-600 whitespace-nowrap">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }) : '-'}
                    </td>
                    <td className="py-3.5 font-semibold text-slate-900">
                      {order.planName || order.planId}
                    </td>
                    <td className="py-3.5 font-bold text-slate-800 whitespace-nowrap">
                      {Number(order.amount).toLocaleString('tr-TR')} {order.currency || 'TRY'}
                    </td>
                    <td className="py-3.5 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        order.status === 'PAID'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : order.status === 'PENDING'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`}>
                        {order.status === 'PAID' ? 'Ödendi' : order.status === 'PENDING' ? 'Bekliyor' : 'Başarısız'}
                      </span>
                    </td>
                    <td className="py-3.5 font-mono text-slate-500 text-[11px] whitespace-nowrap">
                      {order.shopierOrderId || '-'}
                    </td>
                    <td className="py-3.5 text-right font-mono text-slate-400 text-[11px] whitespace-nowrap">
                      #{order.id.slice(-8)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Shopier Renewal Modal */}
      <ShopierModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        selectedPlanId={selectedPlanId}
        planName={checkoutTitle}
        amountTRY={checkoutAmount}
        billingPeriod={checkoutPeriod}
        siteId={activeSite?.id}
        onSuccess={() => {
          setIsCheckoutOpen(false);
          loadData();
        }}
      />
    </div>
  );
};
